import {Injectable, Injector} from "@angular/core";

import {ReportError} from "app/app.service";
import {BaseListService} from "app/services/domain/base.service";
import {UsersService} from "app/services/domain/users.service";
import * as Models from "app/services/proxy/model/models";

import {Lookup} from "framework/services/utils.service";
import {MomentHelper} from "framework/ui/formatting/date-format.pipe";
import {Memoizer, ResetMemoizers} from "framework/utils/memoizers";
import moment from "framework/utils/moment";

@Injectable()
export class CurrencyExchangePlanService extends BaseListService<Models.CurrencyExchangeConfigurationListRequest, Models.BatchRequest, Models.CurrencyExchangeConfigurationDefinition>
{
    constructor(inj: Injector,
                private users: UsersService)
    {
        super(inj, Models.CurrencyExchangeConfigurationDefinition);
    }

    @ReportError
    public list(listReq: Models.CurrencyExchangeConfigurationListRequest): Promise<Models.ListResponse>
    {
        return this.domain.apis.currencyExchangePlans.listCurrencyExchangeConfiguration(listReq);
    }


    @ReportError
    protected async getBatchResponseImpl(req: Models.BatchRequest): Promise<Models.CurrencyExchangeConfigurationBatchResponse>
    {
        const recordIdBatchRequest = Models.BatchRequest.newInstance(req);

        return this.domain.apis.currencyExchangePlans.batchCurrencyExchangeConfiguration(recordIdBatchRequest);

    }


    protected createBatchRequest(listReq: Models.CurrencyExchangeConfigurationListRequest,
                                 ids: string[]): Models.BatchRequest
    {
        return Models.BatchRequest.newInstance({ids: ids});
    }

    @ReportError
    @ResetMemoizers
    public async updateCurrencyConfiguration(itemId: string,
                                             definition: Models.CurrencyExchangeConfigurationDefinition): Promise<Models.CurrencyExchangeConfigurationDefinition>
    {
        return await this.domain.apis.currencyExchangePlans.updateCurrencyExchangeConfiguration(itemId, definition);
    }

    @ResetMemoizers
    public async deleteConfiguration(itemId: string): Promise<boolean>
    {
        return await this.domain.apis.currencyExchangePlans.deleteCurrencyExchangeConfiguration(itemId);
    }

    public async viewConfiguration(itemId: string): Promise<Models.CurrencyExchangeConfigurationDefinition>
    {
        return await this.domain.apis.currencyExchangePlans.getCurrencyExchangeConfiguration(itemId);
    }

    @ResetMemoizers
    public async create(currencyExchangeConfigurationDefinition: Models.CurrencyExchangeConfigurationDefinition): Promise<Models.CurrencyExchangeConfigurationDefinition>
    {
        return await this.domain.apis.currencyExchangePlans.createCurrencyExchangeConfiguration(currencyExchangeConfigurationDefinition);
    }

    @Memoizer
    public async fetchAllExchangeRates(): Promise<CurrencyExchangeConfigurationExtended[]>
    {
        try
        {
            const filters = Models.CurrencyExchangeConfigurationFilter.newInstance({});
            const request = Models.CurrencyExchangeConfigurationListRequest.newInstance({
                                                                                            filters: filters
                                                                                        });
            const ids     = await this.domain.apis.currencyExchangePlans.listCurrencyExchangeConfiguration(request);

            if (!ids?.results || ids?.results?.length === 0)
            {
                return [];
            }

            const configs = await this.domain.apis.currencyExchangePlans.batchCurrencyExchangeConfiguration(Models.BatchRequest.newInstance({ids: ids.results}));

            return Object.values(configs.results)
                         .map(c => new CurrencyExchangeConfigurationExtended(c))
                         .filter(c => c.model.is_enabled);
        }
        catch (e)
        {
            return [];
        }
    }

    public async getCurrencyConverter(targetCurrency: Models.Currency): Promise<CurrencyConverter>
    {
        let configs = await this.fetchAllExchangeRates();
        configs     = configs.filter(c =>
                                     {
                                         if (this.users.isAccountAdmin && c.model.scope === Models.CurrencyConversionScope.Account)
                                         {
                                             return c.model.target_currency === targetCurrency;
                                         }

                                         return c.model.target_currency === targetCurrency && c.model.scope === Models.CurrencyConversionScope.Tenant;
                                     });

        return new CurrencyConverter(configs);
    }

    public async getOrganizationalCurrencyFromAvailableCurrencies(availableCurrenciesByTenant: Lookup<Models.Currency[]>): Promise<Models.Currency>
    {
        const configs       = await this.fetchAllExchangeRates();
        const accountConfig = configs.find(c => c.model.scope === Models.CurrencyConversionScope.Account && c.model.master_account_id === this.users.masterAccountId);

        if (this.users.isAccountAdmin && accountConfig)
        {
            let hasSourceCurrency = false;
            for (const sourceCurrencies of Object.values(availableCurrenciesByTenant || {}))
            {
                for (const sourceCurrency of sourceCurrencies || [])
                {
                    hasSourceCurrency = true;
                    if (!accountConfig.hasCurrency(sourceCurrency)) return null;
                }
            }

            if (hasSourceCurrency)
            {
                return accountConfig.model.target_currency;
            }
            return null;
        }

        const tenantConfigByTenantId = new Map<string, CurrencyExchangeConfigurationExtended>();
        for (const config of configs)
        {
            if (config.model.scope !== Models.CurrencyConversionScope.Tenant) continue;
            for (const tenantId of config.model.tenant_ids || [])
            {
                tenantConfigByTenantId.set(tenantId, config);
            }
        }

        let targetCurrency: Models.Currency = null;
        for (const [tenantId, sourceCurrencies] of Object.entries(availableCurrenciesByTenant || {}))
        {
            const config = tenantConfigByTenantId.get(tenantId);

            if (!config || !(sourceCurrencies || []).every(curr => config.hasCurrency(curr))) return null;

            if (!targetCurrency)
            {
                targetCurrency = config.model.target_currency;
            }

            if (targetCurrency !== config.model.target_currency) return null;
        }

        return targetCurrency;
    }
}


type ConversionFunction = (value: number,
                           timestamp: number) => number;

export class CurrencyConverter
{
    constructor(private configs: CurrencyExchangeConfigurationExtended[])
    {
    }

    public getConverter(tenantId: string,
                        sourceCurrency: Models.Currency): ConversionFunction
    {
        const config = this.configs.find(c => c.model.scope !== Models.CurrencyConversionScope.Tenant || c.model.tenant_ids.includes(tenantId));
        return (value: number,
                timestamp: number): number =>
        {
            const sourceRate = config.getExchangeRate(sourceCurrency, timestamp);

            return value * sourceRate;
        };
    }

    static getMonthYearFormat(date: Date)
    {
        return MomentHelper.parse(date)
                           .utc()
                           .format("MM/YYYY");
    }

    static convertToUTC(date: Date): Date
    {
        return moment(date)
            .utc()
            .toDate();
    }

    static getStartDateOfMonth(date: Date): Date
    {
        return moment(date)
            .utc()
            .startOf("month")
            .toDate();
    }

    static getPreviousMonthLastDate(date: Date): Date
    {
        return moment(date)
            .utc()
            .subtract(1, "month")
            .endOf("month")
            .toDate();
    }

    static addAdditionalPeriod(currencyList: Models.Currency[],
                               startDate: Date): Models.CurrencyRateConfiguration
    {
        const basicConfiguration: Models.ExchangeCurrencyRate[] = [];

        for (const currency of currencyList)
        {
            const exchangeMapping = Models.ExchangeCurrencyRate.newInstance({
                                                                                exchange_rate  : null,
                                                                                source_currency: Models.Currency[currency]
                                                                            });
            basicConfiguration.push(exchangeMapping);
        }
        const newStartDate = CurrencyConverter.getStartDateOfMonth(startDate);
        return Models.CurrencyRateConfiguration.newInstance({
                                                                start_date   : newStartDate,
                                                                currency_rate: basicConfiguration
                                                            });
    }
}

export class CurrencyExchangeConfigurationExtended
{
    private readonly m_sourceCurrencies: Set<Models.Currency>;

    constructor(public readonly model: Models.CurrencyExchangeConfigurationDefinition)
    {
        this.m_sourceCurrencies = new Set<Models.Currency>();

        const config = model.configurations[0];
        for (const rate of config.currency_rate)
        {
            this.m_sourceCurrencies.add(rate.source_currency);
        }
    }


    public hasCurrency(sourceCurrency: Models.Currency): boolean
    {
        return this.m_sourceCurrencies.has(sourceCurrency) || sourceCurrency === this.model.target_currency;
    }


    public getExchangeRate(sourceCurrency: Models.Currency,
                           timestamp: number): number
    {
        for (let i = 0; i < this.model.configurations.length; i++)
        {
            const config = this.model.configurations[i];
            const isLast = i === this.model.configurations.length - 1;
            if (timestamp >= config.start_date.valueOf() || isLast)
            {
                for (const rate of config.currency_rate)
                {
                    if (rate.source_currency === sourceCurrency)
                    {
                        return rate.exchange_rate;
                    }
                }
                break;
            }
        }

        throw Error("Exchange rate not found for source currency: " + sourceCurrency);
    }
}
